import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, useToast, Badge, DataTable, Column, EmptyState, Card, NameCell } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import {
  Employee,
  ImportAction,
  ImportPreview,
  ImportResultRow,
  ImportRowInput,
  OfficeNetwork,
} from '../../../shared/services/turnoGeo/types';
import { downloadEmployeesTemplate, downloadImportResults, parseEmployeesFile } from '../employeeExcel';

const repo = getTurnoGeoRepository();
const CHUNK = 4;

const ACTION_TONE: Record<ImportAction, 'success' | 'info' | 'neutral' | 'critical'> = { CREAR: 'success', VINCULAR: 'info', OMITIR: 'neutral', ERROR: 'critical' };
const ACTION_LABEL: Record<ImportAction, string> = { CREAR: 'Crear cuenta', VINCULAR: 'Vincular', OMITIR: 'Omitir', ERROR: 'Error' };
const OUTCOME_TONE = { CREADO: 'success', VINCULADO: 'info', OMITIDO: 'neutral', ERROR: 'critical' } as const;

type Step = 'lista' | 'revision' | 'ejecutando' | 'fin';

/**
 * Configuración > Empleados. Carga masiva desde Excel: se revisa primero (no escribe nada),
 * luego se crean las cuentas en tandas pequeñas y al final se descarga un Excel con las claves
 * iniciales. Las claves solo existen en esta pantalla: el servidor no las guarda.
 */
export function EmployeesTab() {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('lista');
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [filter, setFilter] = useState('');
  const [showSalary, setShowSalary] = useState(false);

  const [rows, setRows] = useState<ImportRowInput[]>([]);
  const [fileName, setFileName] = useState('');
  const [ignored, setIgnored] = useState<string[]>([]);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ImportResultRow[]>([]);

  const loadEmployees = useCallback(() => {
    repo.listEmployees().then(setEmployees).catch((e) => {
      setEmployees([]);
      toast.show(e instanceof Error ? e.message : 'No se pudo cargar los empleados', 'error');
    });
  }, [toast]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const reset = () => {
    setStep('lista'); setRows([]); setPreview(null); setResults([]); setProgress(0); setFileName(''); setIgnored([]);
    loadEmployees();
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const parsed = await parseEmployeesFile(file);
      const pv = await repo.previewEmployeeImport(parsed.rows);
      setRows(parsed.rows); setIgnored(parsed.ignoredColumns); setFileName(file.name);
      setPreview(pv); setOnlyProblems(false); setStep('revision');
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'No se pudo leer el archivo', 'error');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const run = async () => {
    if (!preview) return;
    // Solo se envían las filas que el servidor no marcó como error ni omitir; el índice original se conserva.
    const todo = preview.rows.filter((r) => r.action === 'CREAR' || r.action === 'VINCULAR').map((r) => r.index);
    setStep('ejecutando'); setProgress(0);
    const all: ImportResultRow[] = [];
    // Las filas con error/omitir también quedan en el resultado.
    preview.rows.filter((r) => r.action === 'ERROR' || r.action === 'OMITIR').forEach((r) =>
      all.push({ index: r.index, item: r.item, documentNumber: r.documentNumber, fullName: r.fullName, email: r.email,
        outcome: r.action === 'ERROR' ? 'ERROR' : 'OMITIDO', message: r.errors.join(' · ') || 'Sin cuenta (estado no activo).' }));
    for (let i = 0; i < todo.length; i += CHUNK) {
      const idxs = todo.slice(i, i + CHUNK);
      try {
        // Cada tanda lleva sus propias filas; el servidor numera desde startIndex.
        const chunk = idxs.map((ix) => rows[ix]);
        const res = await repo.executeEmployeeImport(chunk, idxs[0]);
        // El servidor numera consecutivo desde startIndex: se restaura el índice original.
        res.forEach((r, k) => all.push({ ...r, index: idxs[k] ?? r.index }));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error de red';
        idxs.forEach((ix) => {
          const p = preview.rows.find((r) => r.index === ix)!;
          all.push({ index: ix, item: p.item, documentNumber: p.documentNumber, fullName: p.fullName, email: p.email, outcome: 'ERROR', message: `No se procesó (${msg}). Vuelve a cargar el archivo: es seguro repetirlo.` });
        });
      }
      setProgress(Math.min(i + CHUNK, todo.length) / Math.max(todo.length, 1));
    }
    all.sort((a, b) => a.index - b.index);
    setResults(all); setStep('fin');
    if (all.some((r) => r.outcome === 'CREADO')) {
      try { downloadImportResults(all); } catch { /* el botón de descarga sigue disponible */ }
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q || !employees) return employees ?? [];
    return employees.filter((e) => [e.fullName, e.email, e.documentNumber, e.process, e.costCenter, e.city].some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [employees, filter]);

  const [editing, setEditing] = useState<Employee | null>(null);
  const [allSedes, setAllSedes] = useState<OfficeNetwork[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const openSedes = (e: Employee) => {
    setEditing(e);
    setPicked((e.sedes ?? []).map((s) => s.id));
    repo.listOfficeNetworks().then(setAllSedes).catch(() => setAllSedes([]));
  };
  const saveSedes = async () => {
    if (!editing) return;
    try {
      await repo.updateEmployee(editing.userId, { sedeIds: picked });
      toast.show('Sedes actualizadas.', 'success');
      setEditing(null);
      loadEmployees();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudieron guardar las sedes.', 'error');
    }
  };

  const empColumns: Column<Employee>[] = [
    { key: 'itemNo', header: '#', sortable: true, sortValue: (e) => e.itemNo ?? 0, render: (e) => e.itemNo ?? '' },
    { key: 'documentNumber', header: 'CC', render: (e) => e.documentNumber ?? '' },
    { key: 'fullName', filterValue: (e) => e.fullName, header: 'Nombre', sortable: true, sortValue: (e) => e.fullName, render: (e) => <NameCell name={e.fullName} /> },
    { key: 'email', filterValue: (e) => e.email ?? '', header: 'Correo', render: (e) => e.email ?? '' },
    { key: 'process', filterValue: (e) => e.process ?? '', header: 'Proceso', sortable: true, sortValue: (e) => e.process ?? '', render: (e) => e.process ?? '' },
    { key: 'costCenter', filterValue: (e) => e.costCenter ?? '', header: 'Centro de costos', render: (e) => e.costCenter ?? '' },
    { key: 'city', filterValue: (e) => e.city ?? '', header: 'Ciudad', render: (e) => e.city ?? '' },
    { key: 'contractType', filterValue: (e) => e.contractType ?? '', header: 'Contratación', render: (e) => e.contractType ?? '' },
    {
      key: 'sedes',
      filterValue: (e) => (e.sedes ?? []).map((s) => s.name).join(', ') || 'Remoto · pendiente de definir',
      header: 'Sedes',
      render: (e) => (
        <button type="button" onClick={() => openSedes(e)} className="text-left text-xs hover:underline">
          {(e.sedes ?? []).length > 0 ? (e.sedes ?? []).map((s) => s.name).join(', ') : <span className="text-ink-400">Remoto · pendiente de definir</span>}
        </button>
      ),
    },
    { key: 'employeeStatus', filterValue: (e) => e.employeeStatus, header: 'Estado', render: (e) => <Badge tone={e.employeeStatus === 'ACTIVO' ? 'success' : 'neutral'}>{e.employeeStatus}</Badge> },
    ...(showSalary ? [{ key: 'salary', header: 'Salario', sortable: true, sortValue: (e: Employee) => e.salary ?? 0, render: (e: Employee) => (e.salary == null ? '' : e.salary.toLocaleString('es-CO')) } as Column<Employee>] : []),
  ];

  const shownPreview = preview ? preview.rows.filter((r) => !onlyProblems || r.action === 'ERROR' || r.warnings.length > 0) : [];
  const previewColumns: Column<ImportPreview['rows'][number]>[] = [
    { key: 'index', header: 'Fila', render: (r) => r.item ?? r.index + 1 },
    { key: 'documentNumber', header: 'CC', render: (r) => r.documentNumber },
    { key: 'fullName', header: 'Nombre', render: (r) => <div><div className="font-medium">{r.fullName}</div><div className="text-xs text-gray-500">{r.email}</div></div> },
    { key: 'action', header: 'Acción', render: (r) => <Badge tone={ACTION_TONE[r.action]}>{ACTION_LABEL[r.action]}</Badge> },
    { key: 'obs', header: 'Observaciones', render: (r) => (
      <div className="space-y-0.5 text-xs">
        {r.errors.map((m, i) => <div key={`e${i}`} className="text-red-600">{m}</div>)}
        {r.warnings.map((m, i) => <div key={`w${i}`} className="text-amber-700">{m}</div>)}
        {r.existingUserName && <div className="text-gray-500">Ya existe como {r.existingUserName}</div>}
      </div>
    ) },
  ];

  if (step === 'revision' && preview) {
    const s = preview.summary;
    const doable = s.create + s.link;
    return (
      <div className="space-y-4">
        <Card>
          <div className="space-y-3 p-4">
            <div className="text-sm text-gray-600">Archivo: <b>{fileName}</b> · {s.total} filas. Aún no se ha creado nada.</div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="success">Crear cuenta: {s.create}</Badge>
              <Badge tone="info">Vincular: {s.link}</Badge>
              <Badge tone="neutral">Omitir: {s.skip}</Badge>
              <Badge tone={s.error ? 'critical' : 'neutral'}>Con error: {s.error}</Badge>
            </div>
            {ignored.length > 0 && <div className="text-xs text-gray-500">Columnas no usadas: {ignored.join(', ')}.</div>}
            {s.error > 0 && <div className="rounded bg-red-50 p-2 text-xs text-red-700">Las filas con error no se cargan. Corrige el Excel y vuelve a cargarlo: las ya creadas quedarán como "Vincular" y no se les cambia la clave.</div>}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> Mostrar solo filas con problemas</label>
            <div className="flex gap-2">
              <Button onClick={run} disabled={doable === 0}>Confirmar y cargar {doable} empleado{doable === 1 ? '' : 's'}</Button>
              <Button variant="secondary" onClick={reset}>Cancelar</Button>
            </div>
          </div>
        </Card>
        <DataTable excel fixedKey="fullName" columns={previewColumns} rows={shownPreview} rowKey={(r) => String(r.index)} emptyTitle="Sin filas para mostrar" />
      </div>
    );
  }

  if (step === 'ejecutando') {
    return (
      <Card>
        <div className="space-y-3 p-6">
          <div className="text-sm font-medium">Creando cuentas… no cierres esta pantalla.</div>
          <div className="h-2 w-full overflow-hidden rounded bg-gray-200"><div className="h-2 bg-brand transition-all" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
          <div className="text-xs text-gray-500">{Math.round(progress * 100)}%</div>
        </div>
      </Card>
    );
  }

  if (step === 'fin') {
    const count = (o: ImportResultRow['outcome']) => results.filter((r) => r.outcome === o).length;
    const created = count('CREADO');
    const resultColumns: Column<ImportResultRow>[] = [
      { key: 'index', header: 'Fila', render: (r) => r.item ?? r.index + 1 },
      { key: 'fullName', header: 'Nombre', render: (r) => <div><div className="font-medium">{r.fullName}</div><div className="text-xs text-gray-500">{r.email}</div></div> },
      { key: 'outcome', header: 'Resultado', render: (r) => <Badge tone={OUTCOME_TONE[r.outcome]}>{r.outcome}</Badge> },
      { key: 'message', header: 'Detalle', render: (r) => <span className="text-xs">{r.message}</span> },
    ];
    return (
      <div className="space-y-4">
        <Card>
          <div className="space-y-3 p-4">
            <div className="flex flex-wrap gap-2">
              <Badge tone="success">Creados: {created}</Badge>
              <Badge tone="info">Vinculados: {count('VINCULADO')}</Badge>
              <Badge tone="neutral">Omitidos: {count('OMITIDO')}</Badge>
              <Badge tone={count('ERROR') ? 'critical' : 'neutral'}>Con error: {count('ERROR')}</Badge>
            </div>
            {created > 0 && (
              <div className="rounded bg-amber-50 p-2 text-xs text-amber-800">
                Se descargó un Excel con las claves iniciales. También quedan guardadas cifradas: las consultas con el ojito en Acceso y usuarios. Entrégalas solo a su dueño.
              </div>
            )}
            <div className="flex gap-2">
              {created > 0 && <Button onClick={() => downloadImportResults(results)}>Descargar de nuevo el Excel</Button>}
              <Button variant="secondary" onClick={reset}>Terminar</Button>
            </div>
          </div>
        </Card>
        <DataTable excel fixedKey="fullName" columns={resultColumns} rows={results} rowKey={(r) => String(r.index)} emptyTitle="Sin resultados" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-3 p-4">
          <div className="text-sm text-gray-600">
            Carga masiva desde Excel. Cada persona entra con rol <b>empleado</b> (Marcar, Mis registros, Novedades) y una clave inicial
            <b> Colombia2026 + 4 dígitos</b> (el prefijo se cambia en Parámetros). Las claves quedan guardadas cifradas y el administrador las consulta con el ojito en Acceso y usuarios. Primero se revisa el archivo; no se crea nada hasta confirmar.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={downloadEmployeesTemplate}>Descargar plantilla</Button>
            <Button onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? 'Revisando…' : 'Cargar Excel'}</Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
        </div>
      </Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64"><Input placeholder="Buscar nombre, CC, proceso…" value={filter} onChange={(e) => setFilter(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showSalary} onChange={(e) => setShowSalary(e.target.checked)} /> Mostrar salarios</label>
        <span className="text-xs text-gray-500">{employees ? `${filtered.length} empleado(s)` : ''}</span>
      </div>
      {employees && employees.length === 0
        ? <EmptyState title="Aún no hay empleados cargados" description="Descarga la plantilla, llénala y cárgala." />
        : <DataTable excel fixedKey="fullName" columns={empColumns} rows={filtered} rowKey={(e) => e.id} isLoading={employees === null} emptyTitle="Sin resultados" />}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Sedes de ${editing?.fullName ?? ''}`}>
        <div className="space-y-2">
          {allSedes.length === 0 && <p className="text-xs text-ink-400">No hay sedes creadas.</p>}
          {allSedes.map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={picked.includes(s.id)} onChange={(ev) => setPicked((p) => (ev.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id)))} />
              {s.officeName}
            </label>
          ))}
          <p className="text-xs text-ink-400">Sin sedes marcadas = remoto, pendiente de definir.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="md" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button size="md" onClick={saveSedes}>Guardar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
